import React from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from 'views/components';
import {Translate, translate} from 'views/module';

import backend from 'utils/backend';
import {simplifyQuestions} from 'utils/user_bio';
import {convertToE164} from 'utils/miscellaneous/e164';

const OutreachDialog = (props) => {
  const {caregivers, jobData, jobRn, onClose, onSent, requester} = props;
  const [draft, setDraft] = React.useState(null);
  const [errors, setErrors] = React.useState({});
  const [generateError, setGenerateError] = React.useState();
  const [generating, setGenerating] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const requesterEmail = requester?.email;

  if (!requesterEmail) {
    throw new Error('The logged-in administrator does not have an email address.');
  }

  const generate = React.useCallback(async () => {
    setGenerating(true);
    setGenerateError(undefined);
    try {
      const result = await backend.caregiver.jobOutreach.generate(jobRn, jobData);
      setDraft(result.draft);
    } catch (error) {
      setGenerateError(translate('career.jobDetail.NearbyCaregivers.RecruitingOutreach.GenerateError'));
    } finally {
      setGenerating(false);
    }
  }, [jobData, jobRn]);

  React.useEffect(() => {
    generate();
  }, []);

  const updateDraft = (channel, field, value) => {
    setDraft((current) => ({
      ...current,
      [channel]: {
        ...current?.[channel],
        [field]: value,
      },
    }));
  };

  const sendToCaregiver = async (caregiver) => {
    const caregiverRn = caregiver.caregiverRn;
    const caregiverName = caregiver.caregiverName;
    if (!draft) return null;
    const bio = simplifyQuestions(caregiver.bio?.questions || {});
    const phoneNumber = convertToE164(bio.phoneNumber).e164Format;
    const email = String(bio.email || '').trim();

    if (!phoneNumber) {
      throw new Error(translate(
          'career.jobDetail.NearbyCaregivers.RecruitingOutreach.Errors.PhoneNumber',
          {caregiverName},
      ));
    }

    const channels = await backend.notification.getChannels(caregiverRn);
    const userEmails = channels?.userEmails || channels?.result?.userEmails || [];
    const smsNumbers = channels?.userSmsNumbers || [];
    if (email && !userEmails.some((userEmail) => userEmail.email === email)) {
      await backend.notification.createUserEmail(caregiverRn, email);
    }
    if (!smsNumbers.some((sms) => sms.phoneNumber === phoneNumber)) {
      await backend.notification.createUserSms(caregiverRn, phoneNumber);
    }

    const dialpadLink = await backend.dialpadIntegration.getCaregiverDialpadLink(caregiverRn);
    if (!dialpadLink?.phoneNumber) {
      await backend.dialpadIntegration.createCaregiverDialpadLink(caregiverRn, phoneNumber);
    }
    if (dialpadLink?.phoneNumber && dialpadLink.phoneNumber !== phoneNumber) {
      throw new Error(translate(
          'career.jobDetail.NearbyCaregivers.RecruitingOutreach.Errors.PhoneNumberMismatch',
          {caregiverName},
      ));
    }

    const firstName = caregiverName.split(/\s+/)[0];
    const parsedEmailDraft = {
      ...draft.email,
      subject: draft.email.subject.replace('#{name}', firstName),
      message: draft.email.message.replace('#{name}', firstName),
      fromEmail: 'caregivers@carequilt.ca',
      replyTo: 'caregivers@carequilt.ca',
    };
    const parsedSmsDraft = draft.sms.message.replace('#{name}', firstName);

    const [emailResult, smsResult] = await Promise.allSettled([
      backend.notification.sendEmail(caregiverRn, parsedEmailDraft),
      backend.notification.sendSms(caregiverRn, parsedSmsDraft, requesterEmail),
    ]);
    const sentAt = new Date().toISOString();
    const initialOutreach = {};

    const emailResponse = emailResult.status === 'fulfilled' ? emailResult.value : null;
    const emailStatus = emailResponse?.result?.emailResult?.status;
    const emailMessageRn = emailResponse?.result?.messageRn || null;

    if (emailStatus !== 'SUCCESS') {
      throw new Error(
          translate(
              'career.jobDetail.NearbyCaregivers.RecruitingOutreach.Errors.Email',
              {
                caregiverName,
                reason: emailStatus || 'unknown error',
              },
          ),
      );
    }

    const smsResponse = smsResult.status === 'fulfilled' ? smsResult.value : null;
    const smsStatus = smsResponse?.result?.smsResult?.status;

    if (smsStatus && smsStatus !== 'SUCCESS') {
      throw new Error(
          translate(
              'career.jobDetail.NearbyCaregivers.RecruitingOutreach.Errors.Sms',
              {
                caregiverName,
                reason: smsStatus || 'unknown error',
              },
          ),
      );
    }


    if (emailMessageRn) {
      initialOutreach.email = {messageRn: emailMessageRn, sentAt};
    }
    if (Object.keys(initialOutreach).length === 0) {
      throw new Error(translate(
          'career.jobDetail.NearbyCaregivers.RecruitingOutreach.Errors.NoMessage',
          {caregiverName},
      ));
    }

    const result = await backend.caregiver.jobOutreach.update(caregiverRn, jobRn, initialOutreach);
    return result.jobOutreach;
  };

  const handleSend = async () => {
    setSending(true);
    const sent = {};
    const sendErrors = {};
    for (const caregiver of caregivers) {
      // If we expand on this and if an agent won't replace the nearby caregivers feature, perhaps consider sending in parallel in batches
      try {
        sent[caregiver.caregiverRn] = await sendToCaregiver(caregiver);
      } catch (error) {
        sendErrors[caregiver.caregiverRn] = error?.message || translate(
            'career.jobDetail.NearbyCaregivers.RecruitingOutreach.Errors.NoMessage',
            {caregiverName: caregiver.caregiverName || caregiver.caregiverRn},
        );
      }
    }
    setSending(false);
    setErrors((current) => ({...current, ...sendErrors}));
    onSent(sent);
    if (Object.keys(sendErrors).length === 0) onClose();
  };

  const isDraftReady = caregivers.length > 0 && draft?.email?.subject?.trim() &&
    draft?.email?.message?.trim() && draft?.sms?.message?.trim();

  return (
    <Dialog open={true} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle onClose={sending ? undefined : onClose}>
        <Translate expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Title" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{my: 1}}>
          <Typography color="text.secondary">
            <Translate
              expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Recipients"
              substitute={{count: caregivers.length}}
            />
          </Typography>
          {
            generateError && <Alert severity="error">{generateError}</Alert>
          }
          {
            !draft && generating &&
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">
                <Translate expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Generating" />
              </Typography>
            </Stack>
          }
          {
            draft &&
            <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2}}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">
                    <Translate expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Preview" />
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={generate}
                    disabled={generating || sending}
                    loading={generating}
                  >
                    <Translate expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Regenerate" />
                  </Button>
                </Stack>
                <TextField
                  label={translate('career.jobDetail.NearbyCaregivers.RecruitingOutreach.EmailSubject')}
                  value={draft.email.subject}
                  onChange={(value) => updateDraft('email', 'subject', value)}
                />
                <TextField
                  label={translate('career.jobDetail.NearbyCaregivers.RecruitingOutreach.EmailMessage')}
                  value={draft.email.message}
                  onChange={(value) => updateDraft('email', 'message', value)}
                  multiline
                  minRows={5}
                />
                <TextField
                  label={translate('career.jobDetail.NearbyCaregivers.RecruitingOutreach.SmsMessage')}
                  value={draft.sms.message}
                  onChange={(value) => updateDraft('sms', 'message', value)}
                  multiline
                  minRows={3}
                />
                <Typography variant='footer' color='textSecondary' sx={{fontSize: 12}}>
                  <Translate expression='career.jobDetail.NearbyCaregivers.RecruitingOutreach.Footer' />
                </Typography>
              </Stack>
            </Box>
          }
          {
            Object.values(errors).map((error, index) => <Alert key={index} severity="error">{error}</Alert>)
          }
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={sending}>
          <Translate expression="generic.Cancel" />
        </Button>
        <Button mode="contained" onClick={handleSend} disabled={!isDraftReady || sending} loading={sending}>
          <Translate expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Send" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

OutreachDialog.propTypes = {
  caregivers: PropTypes.arrayOf(PropTypes.object).isRequired,
  jobData: PropTypes.object.isRequired,
  jobRn: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func.isRequired,
  requester: PropTypes.shape({
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userRn: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(OutreachDialog);
